import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const ISSUE_OPTIONS = [
  "not_found",
  "address_wrong",
  "recipient_unreachable",
  "package_damaged",
  "bus_issue",
  "other",
];

export function ReportProblemModal({
  open,
  onClose,
  onSubmit,
  deliveryId,
  submitting,
  allowRevert = false,
}) {
  const { t } = useTranslation();
  const [issueType, setIssueType] = useState(null);
  const [details, setDetails] = useState("");
  const [revert, setRevert] = useState(false);

  const reset = () => {
    setIssueType(null);
    setDetails("");
    setRevert(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    await onSubmit({
      deliveryId,
      issueType: issueType || "other",
      issueLabel: t(`drive.reportProblem.issues.${issueType || "other"}`),
      details: details.trim(),
      revert: allowRevert && revert,
    });
    reset();
  };

  const canSubmit = !!issueType && !submitting;

  const submitLabel = submitting
    ? t("drive.reportProblem.submitting")
    : revert
    ? t("drive.reportProblem.revertAndSubmit")
    : t("drive.reportProblem.submit");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>{t("drive.reportProblem.title")}</DialogTitle>
      <DialogContent>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1.5 }}
        >
          {t("drive.reportProblem.description")}
        </Typography>

        <ToggleButtonGroup
          orientation="vertical"
          exclusive
          value={issueType}
          onChange={(_, v) => v !== null && setIssueType(v)}
          fullWidth
          sx={{
            gap: 0.75,
            mb: 2,
            "& .MuiToggleButton-root": {
              justifyContent: "flex-start",
              textTransform: "none",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              fontSize: "0.875rem",
              py: 1,
            },
          }}
        >
          {ISSUE_OPTIONS.map((issue) => (
            <ToggleButton key={issue} value={issue}>
              {t(`drive.reportProblem.issues.${issue}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          label={t("drive.reportProblem.detailsLabel")}
          multiline
          minRows={2}
          maxRows={5}
          fullWidth
          size="small"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder={t("drive.reportProblem.detailsPlaceholder")}
        />

        {allowRevert && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={revert}
                  onChange={(e) => setRevert(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("drive.reportProblem.revertLabel")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("drive.reportProblem.revertDescription")}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />

            {revert && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                <Typography variant="caption">
                  {t("drive.reportProblem.revertWarning")}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t("drive.reportProblem.urgentHelp")}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={revert ? "warning" : "primary"}
          disabled={!canSubmit}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
